import { Component, OnDestroy, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { trigger, transition, style, animate } from '@angular/animations'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { UiService } from 'src/app/services/ui.service'
import { AccountService } from '../../services/account.service'
import { ApplicationProviderService } from '../../services/application.service'
import { OnboardingService } from '../../services/onboarding.service'

@Component({
  selector: 'app-account-welcome',
  templateUrl: './account-welcome.component.html',
  styleUrls: ['./account-welcome.component.scss'],
  standalone: false,
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-20px)', height: 0 }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)', height: '*' })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-20px)', height: 0 })),
      ]),
    ]),
  ],
})
export class AccountWelcomeComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>()

  applicationCreated = false
  accountSettingsComplete = false
  accessManagementComplete = false
  allChecksComplete = false

  // Track pulse animations for newly checked items
  applicationCreatedPulse = false
  accountSettingsCompletePulse = false
  accessManagementCompletePulse = false

  constructor(
    public uiService: UiService,
    private accountService: AccountService,
    private apService: ApplicationProviderService,
    private router: Router,
    private onboardingService: OnboardingService
  ) {}

  ngOnInit() {
    this.onboardingService.state$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      // Guard: redirect away if this page shouldn't be shown. This catches stale
      // bookmarks, browser back-button, and race conditions where AccountRootComponent
      // incorrectly routed here (e.g. API error with empty cache).
      if (this.onboardingService.loaded && this.shouldRedirectAway()) {
        this.router.navigateByUrl(this.uiService.buildAccountRootUrl(this.accountId))
        return
      }

      // Backstop seeded-write for accounts that reached /welcome without going through
      // seedNewAccount. Idempotent — no-op if already seeded.
      if (this.onboardingService.loaded) {
        this.onboardingService.markSeededOnboarding()?.subscribe({
          error: (err) => console.warn('Failed to mark account as seeded:', err),
        })
      }

      // Check for newly completed items and trigger pulse
      if (!this.applicationCreated && this.onboardingService.applicationCreated) {
        this.triggerPulse('applicationCreated')
      }
      if (!this.accountSettingsComplete && this.onboardingService.accountSettingsComplete) {
        this.triggerPulse('accountSettingsComplete')
      }
      if (!this.accessManagementComplete && this.onboardingService.accessManagementComplete) {
        this.triggerPulse('accessManagementComplete')
      }

      this.applicationCreated = this.onboardingService.applicationCreated
      this.accountSettingsComplete = this.onboardingService.accountSettingsComplete
      this.accessManagementComplete = this.onboardingService.accessManagementComplete
      this.allChecksComplete = this.onboardingService.allChecksComplete
    })
  }

  /**
   * Determines if the user should be redirected away from the welcome page.
   * Returns true when the intro was previously dismissed, or when the account
   * was never seeded into the welcome flow but already has applications.
   */
  private shouldRedirectAway(): boolean {
    if (this.onboardingService.introDismissed) return true
    // Non-seeded accounts (legacy, including ones with incidental onboarding keys)
    // shouldn't linger on /welcome once they have applications.
    const hasApps = this.apService.loadedApplications().length > 0
    if (!this.onboardingService.isSeededOnboarding && hasApps) return true
    return false
  }

  private triggerPulse(field: 'applicationCreated' | 'accountSettingsComplete' | 'accessManagementComplete') {
    const pulseField = `${field}Pulse` as
      | 'applicationCreatedPulse'
      | 'accountSettingsCompletePulse'
      | 'accessManagementCompletePulse'
    this[pulseField] = true
    // Remove pulse after animation completes
    setTimeout(() => {
      this[pulseField] = false
    }, 2000)
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  get accountId(): string {
    return this.accountService.accountId
  }

  get azureEnabled(): boolean {
    return this.accountService.azureEnabled()
  }

  navigateToCreateApplication() {
    this.router.navigate([this.uiService.buildNewApplicationUrl(this.accountId)], {
      queryParams: { from: 'welcome' },
    })
  }

  navigateToAccountSettings() {
    this.router.navigate([this.uiService.buildAccountSettingsUrl(this.accountId)], {
      queryParams: { from: 'welcome' },
    })
  }

  navigateToTeamAccess() {
    this.router.navigate([this.uiService.buildUserManagementUrl(this.accountId)], {
      queryParams: { from: 'welcome' },
    })
  }

  openDocs() {
    window.open('https://docs.arpio.io', '_blank')
  }

  openSupport() {
    this.uiService.openChat()
  }

  dismissIntro() {
    this.onboardingService.dismissIntro()
    // Navigate to the account root after dismissing
    this.router.navigateByUrl(this.uiService.buildAccountRootUrl(this.accountId))
  }

  toggleAccountSettingsComplete(event: Event) {
    event.stopPropagation()
    this.onboardingService.toggleAccountSettingsComplete()
  }

  toggleAccessManagementComplete(event: Event) {
    event.stopPropagation()
    this.onboardingService.toggleAccessManagementComplete()
  }

  // DEV ONLY: Uncomment for testing onboarding flow reset
  // resetOnboarding() {
  //   this.onboardingService.resetState()
  // }
}
